<template>
  <div class="dashboard-wrapper page-gutter">

    <!-- Header -->
    <Section
      :title="detectionZone?.DetectionZoneName
                ? $t('demo.zone.title', { zone: detectionZone.DetectionZoneName })
                : $t('demo.zone.titlePlain')"
      :badge="openingHoursMode"
      :accent="zoneColor"
    >
      <!-- Filters -->
      <ControlHead>
        <duration-dropdown
          :dateRangeMode="dateRangeMode"
          :selectedDuration="duration"
          :disabledDurations="disabledDurations"
        />
        <dropdown-opening-hours-mode
          :openingHoursMode="openingHoursMode"
          :disabledModes="disabledOpeningHourModes"
        />

        <template #actions>
          <button
                    class="toggle-popup"
                    :disabled="isInsightsDisabled"
                    @click="!isInsightsDisabled && toggleAllensInsights()"
                    :title="isInsightsDisabled ? $t('demo.zone.unavailableInDemo') : $t('demo.zone.openInsights')"
                >
                    <i class="bi bi-lightning-charge"></i> {{ $t('demo.zone.allenInsights') }}
                </button>
        </template>
      </ControlHead>

      <!-- Zone identity 6.2: the zone changed type today — its figures are still those
           of the old type until the nightly run, so none are shown here. -->
      <!-- 6.7: settings saved today that this page's figures follow only tonight. -->
      <ChangeNotice v-if="!typeChange && openingHoursChanged" :compact="true" :text="NOTICES.settings.opening_hours" />
      <ChangeNotice v-if="!typeChange && thresholdChanged" :text="NOTICES.settings.thresholds" />
      <ChangeNotice
        v-if="typeChange"
        :text="NOTICES.zonePage"
        :detail="$t('demo.zone.typeChanged', { to: zoneTypeLabel(ZONE_TYPE_NAMES[typeChange.to]), from: zoneTypeLabel(ZONE_TYPE_NAMES[typeChange.from]) })"
      />

      <template v-if="!typeChange">
      <div v-if="sameIdInOutSetting" class="same-id-note">
        <i class="bi bi-exclamation-triangle"></i>
        {{ $t('demo.zone.sameId') }}
      </div>

      <!-- Per-zone weather response — shown for EVERY zone (complete data view),
           incl. an honest "no clear weather link" result; crossings label for tripwires. -->
      <StoralyticDemoZoneWeatherCard
        :zone-name="zoneName"
        :use-crossings="useCrossingsLabel"
        style="margin: 14px 0 2px;"
      />

      <!-- Checkout queue — length of the line in this checkout's waiting zone
           (from zone-occupancy overlap). Only for Checkout Waiting zones. -->
      <div v-if="checkoutQueue" class="checkout-queue-card">
        <div class="cq-head">
          <i class="bi bi-people"></i>
          <span>{{ $t('demo.zone.queue.title') }}</span>
        </div>
        <p class="cq-body">
          <strong class="cq-verdict">{{ checkoutQueueVerdict }}</strong> {{ $t('demo.zone.queue.body', { pct: Math.round(checkoutQueue.pct_time_ge2), peak: checkoutQueue.peak_line }) }}
        </p>
      </div>

      <!-- Tripwire-only zone layout (renders when zone has a tripwire and no polygon).
           Mirrors production DetectionZoneData.vue tripwire-layout-grid. -->
      <div
        v-if="useTripwireMode"
        class="tripwire-layout-grid"
        :class="{ 'tripwire-hourly': tripwireGranularity === 'hourly' }"
        style="margin-top:16px;"
      >
        <!-- Row 1, left: Crossings line chart -->
        <ChartPanel
          class="tw-area-crossings"
          :title="crossingsPanelTitle"
          icon="bi-arrow-left-right"
          :height="360"
          :note="tripwireMarkerCaption || ''"
          :empty="!tripwireChartData"
          :accent="zoneColor"
        >
          <div class="grid-panel-toolbar tripwire-toolbar">
            <div class="tripwire-totals">
              <span class="tw-pill tw-in">
                <i class="bi bi-arrow-down-right"></i>
                {{ tripwireInLabel }}: <b>{{ num(results?.tripwire_total_in || 0) }}</b>
              </span>
              <span class="tw-pill tw-out">
                <i class="bi bi-arrow-up-right"></i>
                {{ tripwireOutLabel }}: <b>{{ num(results?.tripwire_total_out || 0) }}</b>
              </span>
            </div>
            <data-granularity style="margin-top: 6px;" @updateDataGranularity="updateTripwireGranularity" />
            <label v-if="tripwireGranularity === 'daily'" class="wx-overlay-toggle" :title="$t('demo.zone.weather.crossingsTip')">
              <input type="checkbox" v-model="weatherOverlay" />
              <i class="bi bi-thermometer-half"></i> {{ $t('demo.zone.weather.toggle') }}
            </label>
          </div>
          <ChartLineTripwireCrossingsDetectionZone
            v-if="tripwireChartData"
            :chart-data="tripwireChartData"
            :timeSpan="durationText"
            :in-label="tripwireInLabel"
            :out-label="tripwireOutLabel"
            :show-weather="weatherOverlay && tripwireGranularity === 'daily'"
            :weather-by-date="tripwireWeatherByDate"
            :markers="zoneMarkers"
          />
        </ChartPanel>

        <!-- Row 1, right: Top Crossing Highlights -->
        <ChartPanel
          class="tw-area-highlights tw-compact panel-top-visitors"
          :title="topHighlightsTitle"
          icon="bi-calendar2-event"
          :height="360"
          :empty="!results"
          :accent="zoneColor"
        >
          <ChartTopVisitorHighlights
            :days="effectiveFormattedTopVisitorDays"
            :maxDayCount="effectiveMaxDayCount"
            :hours="effectiveFormattedTopVisitorHours"
            :maxHourCount="effectiveMaxHourCount"
            :busiestAvgHours="effectiveFormattedBusiestAvgHours"
            :maxAvgVisitorsPerHour="effectiveMaxAvgPerHour"
            :zoneColor="zoneColor"
            :timeSpan="durationText"
          />
        </ChartPanel>

        <!-- Row 2, left: Heatmap (tripwire-sourced) -->
        <ChartPanel
          class="tw-area-heatmap"
          :title="$t('demo.zone.panel.heatmap')"
          icon="bi-grid-3x3-gap"
          :height="520"
          :empty="!effectiveHeatmapData"
          :accent="zoneColor"
        >
          <HeatmapDetectionZone
            :heatmapChartData="effectiveHeatmapData"
            :timeSpan="durationText"
            :detectionZoneTypeName="detectionZoneTypeName"
          />
        </ChartPanel>

        <!-- Right column: % of Total, Mode C dwell, Trends, Avg comparison -->
        <div class="tw-area-trends-stack">
          <!-- % of Total Visitors hidden for Entrance/Exit/Combined EE
               (their inbound is the store total). -->
          <ChartPanel
            v-if="![1, 2, 3].includes(detectionZoneTypeId)"
            class="tw-compact"
            :title="$t('demo.zone.panel.pctOfTotal')"
            icon="bi-pie-chart"
            :height="140"
            :empty="!totalStoreVisitors"
            :emptyText="$t('demo.zone.panel.pctOfTotalEmpty')"
            :accent="zoneColor"
          >
            <div class="pct-of-total">
              <div class="pct-value" :style="{ color: zoneColor }">
                {{ pctText(tripwirePctOfTotal) }}
              </div>
              <div class="pct-context">
                {{ $t('demo.zone.panel.pctOfTotalContext', { n: num(results?.tripwire_total_in || 0), total: num(totalStoreVisitors) }) }}
              </div>
            </div>
          </ChartPanel>

          <!-- Estimated Avg Dwell (Mode C — Little's Law). Only shown when
               the zone opts in via DetectionZoneSettings.UseTripwireForDwell.
               For entrance / exit / Combined-EE zones the metric is total
               in-store visit length, so the title and explainer swap. -->
          <ChartPanel
            v-if="useTripwireForDwell"
            class="tw-compact"
            :title="dwellPanelTitle"
            icon="bi-stopwatch"
            :height="140"
            :empty="hideDwellInAllMode || !results?.tripwire_dwell_estimate_seconds"
            :emptyText="hideDwellInAllMode ? dwellAllModeNote : $t('demo.zone.dwell.empty')"
            :accent="zoneColor"
          >
            <div class="dwell-estimate">
              <div class="dwell-value-row">
                <span class="dwell-value" :style="{ color: zoneColor }">
                  {{ formatDwellSeconds(results.tripwire_dwell_estimate_seconds) }}
                </span>
                <!-- See note in DetectionZoneData.vue — confidence
                     chip is self-describing; no Tooltip needed. -->
                <span
                  v-if="results?.tripwire_dwell_confidence"
                  :class="['dwell-conf', `conf-${results.tripwire_dwell_confidence}`]"
                >
                  {{ $t(`demo.zone.dwell.confidence.${results.tripwire_dwell_confidence}`) }}
                </span>
              </div>
              <div v-if="isEntranceLikeZoneType" class="dwell-context">
                <i class="bi bi-info-circle"></i>
                {{ $t('demo.zone.dwell.storeContext') }}
              </div>
              <!-- See note in DetectionZoneData.vue — dwellMethodTooltip
                   removed (was outdated copy, line is self-explanatory). -->
              <div class="dwell-context">
                <i class="bi bi-info-circle"></i>
                {{ $t('demo.zone.dwell.basedOn', { n: results.tripwire_dwell_sample_days || 0 }, results.tripwire_dwell_sample_days === 1 ? 1 : 2) }}
              </div>
            </div>
          </ChartPanel>

          <ChartPanel
            class="tw-compact panel-trends"
            :title="trendsPanelTitle"
            icon="bi-activity"
            :empty="!effectiveTrendTotal"
            :accent="zoneColor"
            :height="'auto'"
          >
            <ChartVisitorTrend
              v-if="effectiveTrendTotal"
              :trend-total="effectiveTrendTotal"
              :trend-walkbys="null"
              :trend-shorts="null"
              :trend-clears="null"
              :zoneColor="zoneColor"
              :timeSpan="durationText"
              :detectionZoneTypeName="detectionZoneTypeName"
              :durationSegmentationStrategy="results?.visitors_duration_segmentation?.strategy || 'insufficient_data'"
              :totalTrendLabel="useCrossingsLabel ? $t('demo.zone.panel.totalInboundTrend') : $t('demo.zone.panel.totalVisitorTrend')"
              :hideSegmentationTrends="true"
            />
          </ChartPanel>

          <ChartPanel
            class="tw-compact"
            :title="avgComparisonTitle"
            icon="bi-bar-chart"
            :height="220"
            :empty="!results || effectiveAvgPerDay == null || effectiveAvgSameWeekday == null"
            :accent="zoneColor"
          >
            <ChartAvgVisitorComparison
              v-if="effectiveAvgPerDay != null && effectiveAvgSameWeekday != null"
              :avg-all="effectiveAvgPerDay"
              :avg-same-weekday="effectiveAvgSameWeekday"
              :zoneColor="zoneColor"
              :timeSpan="durationText"
              :currentWeekday="results.current_weekday ?? 0"
            />
          </ChartPanel>
        </div>
      </div>

      <!-- Polygon / polygon+tripwire layout (skipped when tripwire-only takes over) -->
      <template v-if="!useTripwireMode && results && results.visitors_duration_segmentation">
        <!-- Row 1: 3-up until narrow -->
        <div class="chart-grid chart-grid--3" style="margin-top: 12px;">
          <ChartPanel
            :title="$t('demo.zone.panel.segmentation')"
            icon="bi-people"
            :height="500"
            :empty="!results?.chart_duration_segmentation"
            :accent="zoneColor"
          >
            <ChartVisitorSegmentation
              :results="results"
              :durations="results.chart_duration_segmentation"
              :zoneColor="zoneColor"
              :timeSpan="durationText"
              :detectionZoneTypeName="detectionZoneTypeName"
            />
          </ChartPanel>

          <ChartPanel
            :title="$t('demo.zone.panel.totalTime')"
            icon="bi-clock-history"
            :height="500"
            :empty="!results?.total_time_by_visitor_duration_segmentation"
            :accent="zoneColor"
          >
            <template v-if="results?.visitors_duration_segmentation?.strategy !== 'insufficient_data'">
              <ChartTotalDurationSegmentation
                :durations="results.total_time_by_visitor_duration_segmentation"
                :timeSpan="durationText"
                :detectionZoneTypeName="detectionZoneTypeName"
              />
            </template>
            <template v-else>
              <div class="empty-fallback">{{ $t('demo.zone.panel.totalTimeEmpty') }}</div>
            </template>
          </ChartPanel>

          <ChartPanel
            :title="$t('demo.zone.panel.occupation')"
            icon="bi-activity"
            :height="500"
            :empty="!results?.usage_percentage"
            :accent="zoneColor"
          >
            <ChartZoneOccupation
              :usage="results.usage_percentage"
              :zoneColor="zoneColor"
              :timeSpan="durationText"
            />
          </ChartPanel>
        </div>

        <!-- Row 2 -->
        <div class="row2-grid" style="margin-top:16px;">
          <ChartPanel
            class="row2-item"
            :class="(countsGranularity === 'hourly' || avgDurGranularity === 'hourly') ? 'row2-span-6' : 'row2-span-3'"
            :title="$t('demo.zone.panel.entries')"
            :subtitle="$t('demo.zone.panel.entriesSub')"
            icon="bi-graph-up"
            :height="360"
            :note="countsMarkerCaption || ''"
            :empty="!countsChartData"
            :accent="zoneColor"
          >
            <div class="grid-panel-toolbar">
              <data-granularity style="margin-top: 6px;" @updateDataGranularity="updateCountsGranularity" />
              <label v-if="countsGranularity === 'daily'" class="wx-overlay-toggle" :title="$t('demo.zone.weather.countsTip')">
                <input type="checkbox" v-model="weatherOverlay" />
                <i class="bi bi-thermometer-half"></i> {{ $t('demo.zone.weather.toggle') }}
              </label>
            </div>
            <ChartLineTotalCountsDetectionZone
              v-if="detectionZone && countsChartData"
              :chart-data="countsChartData"
              :zone-name="detectionZone?.DetectionZoneName"
              :timeSpan="durationText"
              :show-weather="weatherOverlay && countsGranularity === 'daily'"
              :weather-by-date="weatherByDate"
              :markers="zoneMarkers"
            />
          </ChartPanel>

          <ChartPanel
            class="row2-item"
            :class="(countsGranularity === 'hourly' || avgDurGranularity === 'hourly') ? 'row2-span-6' : 'row2-span-3'"  
            :title="$t('demo.zone.panel.avgDuration')"
            :subtitle="$t('demo.zone.panel.avgDurationSub')"
            icon="bi-stopwatch"
            :height="360"
            :note="avgDurMarkerCaption || ''"
            :empty="!avgDurChartData"
            :accent="zoneColor"
          >
            <div class="grid-panel-toolbar">
              <data-granularity style="margin-top: 6px;" @updateDataGranularity="updateAvgDurGranularity" />
            </div>
            <ChartLineAverageDurationDetectionZone
              v-if="detectionZone && avgDurChartData"
              :chart-data="avgDurChartData"
              :zone-name="detectionZone?.DetectionZoneName"
              :timeSpan="durationText"
              :markers="zoneMarkers"
            />
          </ChartPanel>
        </div>

        <!-- Row 2.5: Crossings panel for polygon+tripwire combo zones
             (e.g. Garden Utilities). Only renders when the zone has
             both a polygon and tripwire activity, alongside the
             polygon-derived panels above and below. -->
        <ChartPanel
          v-if="hasTripwireActivity"
          :title="crossingsPanelTitle"
          icon="bi-arrow-left-right"
          :height="420"
          :note="tripwireMarkerCaption || ''"
          :empty="!tripwireChartData"
          :accent="zoneColor"
          style="margin-top:16px;"
        >
          <div class="grid-panel-toolbar tripwire-toolbar">
            <div class="tripwire-totals">
              <span class="tw-pill tw-in">
                <i class="bi bi-arrow-down-right"></i>
                {{ tripwireInLabel }}: <b>{{ num(results?.tripwire_total_in || 0) }}</b>
              </span>
              <span class="tw-pill tw-out">
                <i class="bi bi-arrow-up-right"></i>
                {{ tripwireOutLabel }}: <b>{{ num(results?.tripwire_total_out || 0) }}</b>
              </span>
            </div>
            <data-granularity style="margin-top: 6px;" @updateDataGranularity="updateTripwireGranularity" />
            <label v-if="tripwireGranularity === 'daily'" class="wx-overlay-toggle" :title="$t('demo.zone.weather.crossingsTip')">
              <input type="checkbox" v-model="weatherOverlay" />
              <i class="bi bi-thermometer-half"></i> {{ $t('demo.zone.weather.toggle') }}
            </label>
          </div>
          <ChartLineTripwireCrossingsDetectionZone
            v-if="tripwireChartData"
            :chart-data="tripwireChartData"
            :timeSpan="durationText"
            :in-label="tripwireInLabel"
            :out-label="tripwireOutLabel"
            :show-weather="weatherOverlay && tripwireGranularity === 'daily'"
            :weather-by-date="tripwireWeatherByDate"
            :markers="zoneMarkers"
          />
        </ChartPanel>

        <!-- Row 3: Trends -->
        <ChartPanel
          :title="$t('demo.zone.panel.visitorTrends')"
          icon="bi-activity"
          :empty="!(results?.trend_total && results?.trend_walkbys && results?.trend_shorts && results?.trend_clears)"
          :accent="zoneColor"
          :height="'auto'"          
          class="panel-trends" 
          style="margin-top: 16px;"
        >
          <ChartVisitorTrend
            v-if="results?.trend_total && results?.trend_walkbys && results?.trend_shorts && results?.trend_clears"
            :trend-total="results.trend_total"
            :trend-walkbys="results.trend_walkbys"
            :trend-shorts="results.trend_shorts"
            :trend-clears="results.trend_clears"
            :zoneColor="zoneColor"
            :timeSpan="durationText"
            :detectionZoneTypeName="detectionZoneTypeName"
            :durationSegmentationStrategy="results?.visitors_duration_segmentation?.strategy || 'insufficient_data'"
          />
        </ChartPanel>

        <!-- Checkout throughput — this till's own page. WHEN it works, plus the rate.
             Not the store-level barlist: one row is not a comparison.
             Checkout Interaction zones (type 5) only. -->
        <CheckoutTransactionsPanel
          v-if="detectionZoneTypeId === 5"
          :data="checkoutTransactions"
          :zone-name="zoneName"
          :timeSpan="durationText"
          accent="#D3A741"
        />

        <!-- Row 3.5: Service response — "and did anyone come to them?"
             Sits AFTER the zone's own behaviour (counts, duration, trend) because
             it is an outcome of that behaviour, and immediately BEFORE the timing
             block because its own charts answer a timing question. Product (6) and
             Demo (7) zones only — phase 1's scope.
             Renders in all three states incl. not-enabled / not-measurable, which
             show an explanation rather than a zero.
             docs/reference/service_response_measurement.md -->
        <ServiceResponseZonePanel
          v-if="[6, 7].includes(detectionZoneTypeId)"
          :data="serviceResponse"
          :timeSpan="durationText"
          :detectionZoneTypeName="detectionZoneTypeName"
          :accent="zoneColor"
        />

        <!-- Row 4 -->
        <ChartGrid class="zone-row4" style="margin-top: 16px;">
          <ChartPanel
            :title="$t('demo.zone.panel.heatmap')"
            icon="bi-grid-3x3-gap"
            :height="640"
            :empty="!results?.heatmap_chart_data"
            :accent="zoneColor"
          >
            <HeatmapDetectionZone
              :heatmapChartData="results.heatmap_chart_data"
              :timeSpan="durationText"
              :detectionZoneTypeName="detectionZoneTypeName"
            />
          </ChartPanel>

          <!-- Right column: two stacked panels -->
          <div class="chart-vert-stack">
            <ChartPanel
              :title="$t('demo.zone.panel.segCounts')"
              icon="bi-bar-chart"
              :height="310"
              :empty="!results?.daily_lingerer_chart_data || results?.visitors_duration_segmentation?.strategy === 'insufficient_data'"
              :emptyText="'Insufficient data to segment visitor durations per day.'"
              :accent="zoneColor"
            >
              <ChartDurationSegmentationCountsPerDay
                :durations="results.daily_lingerer_chart_data"
                :timeSpan="durationText"
                :detectionZoneTypeName="detectionZoneTypeName"
              />
            </ChartPanel>

            <ChartPanel
              :title="$t('demo.zone.panel.segMix')"
              icon="bi-graph-up"
              :height="310"
              :empty="!results?.daily_lingerer_chart_data || results?.visitors_duration_segmentation?.strategy === 'insufficient_data'"
              :emptyText="'Insufficient data to segment visitor durations per day.'"
              :accent="zoneColor"
            >
              <ChartDurationSegmentationPercentagePerDay
                :durations="results.daily_lingerer_chart_data"
                :timeSpan="durationText"
                :detectionZoneTypeName="detectionZoneTypeName"
              />
            </ChartPanel>
          </div>
        </ChartGrid>

        <!-- Row 5 -->
        <ChartGrid style="margin-top: 16px;">
          <ChartPanel
            :title="$t('demo.zone.panel.topVisitor')"
            icon="bi-calendar2-event"
            class="panel-top-visitors"
            :empty="!results"
            :accent="zoneColor"
          >
            <ChartTopVisitorHighlights
              :days="formattedTopVisitorDays"
              :maxDayCount="results.max_visitors_day"
              :hours="formattedTopVisitorHours"
              :maxHourCount="results.max_visitors_hour"
              :busiestAvgHours="formattedBusiestAvgHours"
              :maxAvgVisitorsPerHour="results.max_avg_visitors_per_hour"
              :zoneColor="zoneColor"
              :timeSpan="durationText"
            />
          </ChartPanel>

          <ChartPanel
            :title="$t('demo.zone.panel.avgVisitor')"
            icon="bi-bar-chart"
            :height="220"
            :empty="!results"
            :accent="zoneColor"
          >
            <ChartAvgVisitorComparison
              :avg-all="results.avg_visitors_per_day"
              :avg-same-weekday="results.avg_visitors_same_weekday"
              :zoneColor="zoneColor"
              :timeSpan="durationText"
              :currentWeekday="results.current_weekday"
            />
          </ChartPanel>
        </ChartGrid>
      </template>
      <!-- /guard -->
      </template>

    </Section>

    <!-- loader overlay -->
    <div v-if="isLoading" class="loader-overlay">
      <div class="loader"></div>
    </div>
  </div>
</template>

<script>
    import { onMounted, onUnmounted, ref, computed, watch } from 'vue'

    import { detection_zone_colors_by_name_solid } from '@/constants.js'

    import StoreAnalyticsByAllen from './StoreAnalyticsByAllen.vue'
    import ChartTotalDurationSegmentation from './ChartTotalDurationSegmentation.vue'
    import ChartDurationSegmentationCountsPerDay from './ChartDurationSegmentationCountsPerDay.vue'
    import ChartDurationSegmentationPercentagePerDay from './ChartDurationSegmentationPercentagePerDay.vue'
    import HeatmapDetectionZone from './HeatmapDetectionZone.vue'
    import ChartLineTotalCountsDetectionZone from './ChartLineTotalCountsDetectionZone.vue'
    import ChartLineAverageDurationDetectionZone from './ChartLineAverageDurationDetectionZone.vue'
    import ChartVisitorSegmentation from './ChartVisitorSegmentation.vue'
    import ChartZoneOccupation from './ChartZoneOccupation.vue'
    import ChartAvgVisitorComparison from './ChartAvgVisitorComparison.vue'
    import ChartTopVisitorHighlights from './ChartTopVisitorHighlights.vue'
    import ChartVisitorTrend from './ChartVisitorTrend.vue'
    import ChartLineTripwireCrossingsDetectionZone from './ChartLineTripwireCrossingsDetectionZone.vue'
    import { loadDemoJSON } from '@/api/demoData.js'
    import { hasAnyTripwire } from '@/utils/tripwireShape'

    import DurationDropdown from './DurationDropdown.vue'
    import DataGranularity from './DataGranularityRadioButton.vue'
    import DropdownOpeningHoursMode from './DropdownOpeningHoursMode.vue'

    // shared UI
    import Section from '@/components/ui/Section.vue'
    import StoralyticDemoZoneWeatherCard from './StoralyticDemoZoneWeatherCard.vue'
    import ControlHead from '@/components/ui/ControlHead.vue'
    import ChartGrid from '@/components/ui/ChartGrid.vue'
    import Tooltip from '@/components/ui/Tooltip.vue'
    import ChartPanel from '@/components//ui/ChartPanel.vue'
    import ServiceResponseZonePanel from '@/components/ServiceResponseZonePanel.vue'
    import CheckoutTransactionsPanel from '@/components/CheckoutTransactionsPanel.vue'
    import ChangeNotice from '@/components/ui/ChangeNotice.vue'
    import { useRoute } from 'vue-router'
    import { NOTICES, ZONE_TYPE_NAMES, typeChangeOf, settingChanged } from '@/zones/changesToday'
    import { registerFor, demoMarkersFor } from '@/demo/changesTodayDemo'
    import { markerCaption } from '@/zones/zoneMarkers'
    import { useI18n } from 'vue-i18n'
    import { zoneTypeLabel } from '@/i18n/zoneTypes'
    import { formatDuration, formatNumber, formatPercent } from '@/i18n/format'
    import { currentLanguage } from '@/i18n'


    export default {
        components: {
            Tooltip,
            ServiceResponseZonePanel,
            CheckoutTransactionsPanel,
            StoreAnalyticsByAllen,
            ChartTotalDurationSegmentation,
            ChartDurationSegmentationCountsPerDay,
            ChartDurationSegmentationPercentagePerDay,
            HeatmapDetectionZone,
            ChartLineTotalCountsDetectionZone,
            ChartLineAverageDurationDetectionZone,
            ChartVisitorSegmentation,
            ChartZoneOccupation,
            ChartAvgVisitorComparison,
            ChartTopVisitorHighlights,
            ChartVisitorTrend,
            DurationDropdown,
            DataGranularity,
            DropdownOpeningHoursMode,
            ChartLineTripwireCrossingsDetectionZone,
            Section,
            ChangeNotice,
            StoralyticDemoZoneWeatherCard,
            ControlHead,
            ChartGrid,
            ChartPanel,
        },
        
        props: {
            zoneName: { type: String, required: true }
        },

        setup(props, { emit }) {
            const { t } = useI18n()
            const tr = t
            // Dutch number formats (1.234 · 11,1%); English shows the raw values as before.
            const num = (n) => (currentLanguage() === 'nl' ? formatNumber(n) : n)
            const pctText = (p) => (currentLanguage() === 'nl' ? formatPercent(p, { decimals: 1 }) : `${p.toFixed(1)}%`)
            // Date parts in the portal's language: English keeps en-GB exactly as before.
            const dateLocale = () => (currentLanguage() === 'nl' ? 'nl' : 'en-GB')
            // "14h–15h" (English, as before) / "14:00–15:00" (Dutch).
            const hourSpan = (hour, nextHour) => (currentLanguage() === 'nl'
                ? `${String(hour).padStart(2, '0')}:00–${String(nextHour).padStart(2, '0')}:00`
                : `${hour}h–${nextHour}h`)
            // ?scenario=type_change: this zone changed type today (demo of phase 6.2).
            const route = useRoute()
            const typeChange = computed(() => typeChangeOf(registerFor(route.query), { name: props.zoneName }))
            const openingHoursChanged = computed(() => settingChanged(registerFor(route.query), 'opening_hours'))
            const thresholdChanged = computed(() => {
                const reg = registerFor(route.query)
                const z = detectionZone.value
                return !!(z && ((reg.settings.thresholds || {}).zones || []).length
                          && settingChanged(reg, 'thresholds', z.ZoneKey ?? z.DetectionZoneId))
            })
            const allZones = ref({})
            const detectionZone = ref("")
            const detectionZoneTypeId = ref(null)
            // This zone's row out of the same file the store view reads, so the page and
            // the section can never disagree about the same counter.
            const detectionzonetypes = ref([])
            const detectionZoneTypeName = ref("")
            // Zone-level service response (docs/reference/service_response_measurement.md).
            // null until loadData decides; {enabled:false} renders the not-enabled state.
            const serviceResponse = ref(null)
            const checkoutTransactions = ref(null)
            const sameIdInOutSetting = ref(false)

            const results = ref({})
            const totalStoreVisitors = ref(0)
            const conversionRate = ref(0.1)
            const productValue = ref(0)

            const storeAnalytics = ref("")
            const showAllensInsights = ref(false)

            const dateRangeMode = ref('duration')
            const startDate = ref(null)
            const endDate = ref(null)
            const countsGranularity   = ref('daily')
            const avgDurGranularity   = ref('daily')
            const duration = ref(30)
            const disabledDurations = ref([])
            const durationText = computed(() => t('demo.zone.last30'))
            const openingHoursMode = ref('only_regular_opening_hours')
            // Dwell is a while-we're-open metric. Outside opening hours the entrance still
            // logs staff (opening up / closing) — those crossings aren't visits and would
            // dilute the arrival rate, overstating dwell. So we don't show a dwell figure in
            // "All data" mode, only under the opening-hours views. Counts stay all=all; this
            // gate is dwell-only. (See the store-dwell root-cause analysis, cid-8 Aug 2026.)
            const hideDwellInAllMode = computed(() => openingHoursMode.value === 'all')
            const dwellAllModeNote = computed(() => t('demo.zone.dwell.allModeNote'))
            const disabledOpeningHourModes = ref([])
            
            const isLoading = ref(false)

            const isMounted = ref(true)

            const isInsightsDisabled = true

            const countsChartData = computed(() =>
                countsGranularity.value === 'daily'
                    ? results.value.daily_chart_and_list_data
                    : results.value.hourly_chart_and_list_data
            )
            const avgDurChartData = computed(() =>
                avgDurGranularity.value === 'daily'
                    ? results.value.daily_chart_and_list_data
                    : results.value.hourly_chart_and_list_data
            )

            // --- Weather overlay on the Total Counts chart (demo) ---------------
            // Per-zone coupling: which way this aisle's traffic moves with warmth.
            // +1 rides the heat, -1 bucks it, 0 = weather-flat (see the per-zone card).
            const WX_COUPLING = {
                'Garden Utilities': 1,
                'Power Tools': -1,
                'Promoted Articles Zone': -0.6,
            }
            const weatherOverlay = ref(true)
            // Demo-only: synthesise a plausible daytime-temperature series that
            // visibly tracks (or, for flat zones, deliberately does NOT track) this
            // zone's real counts, so the overlay tells the zone's true weather story.
            // Production wires scan_company_weather_hourly instead.
            const weatherByDate = computed(() => {
                const data = countsChartData.value
                if (!data || countsGranularity.value !== 'daily') return null
                const zn = props.zoneName
                const entries = Object.entries(data).filter(([, z]) => z && z[zn])
                if (!entries.length) return null
                const counts = entries.map(([, z]) => z[zn].zone_in_count ?? 0)
                const mean = counts.reduce((a, b) => a + b, 0) / counts.length
                const spread = Math.max(1, Math.max(...counts) - Math.min(...counts))
                const coupling = WX_COUPLING[zn] ?? 0
                const out = {}
                entries.forEach(([date], i) => {
                    let t
                    if (coupling !== 0) {
                        // temperature rides (or mirrors) the count series → visible link
                        t = 24 + coupling * 8 * ((counts[i] - mean) / spread)
                    } else {
                        // weather-flat zone: an independent seasonal wiggle
                        t = 23 + 5 * Math.sin(i / 2.3) + 2 * Math.cos(i / 5.0)
                    }
                    out[date] = Math.round(Math.max(14, Math.min(34, t)))
                })
                return out
            })
            // Same derivation for the tripwire crossings chart (flat key → {zone_in_count}).
            const tripwireWeatherByDate = computed(() => {
                const data = tripwireChartData.value
                if (!data || tripwireGranularity.value !== 'daily') return null
                const keys = Object.keys(data)
                if (!keys.length) return null
                const counts = keys.map(k => data[k]?.zone_in_count ?? 0)
                const mean = counts.reduce((a, b) => a + b, 0) / counts.length
                const spread = Math.max(1, Math.max(...counts) - Math.min(...counts))
                const coupling = WX_COUPLING[props.zoneName] ?? 0
                const out = {}
                keys.forEach((k, i) => {
                    let t
                    if (coupling !== 0) t = 24 + coupling * 8 * ((counts[i] - mean) / spread)
                    else t = 23 + 5 * Math.sin(i / 2.3) + 2 * Math.cos(i / 5.0)
                    out[k] = Math.round(Math.max(14, Math.min(34, t)))
                })
                return out
            })

            const zoneColor = computed(() => {
              if (!detectionZone.value?.DetectionZoneTypeId) return '#000000'
              const typeMatch = detectionzonetypes.value.find(
                t => t.DetectionZoneTypeId === detectionZone.value.DetectionZoneTypeId
              )
              const name = typeMatch?.DetectionZoneTypeName || ''
              detectionZoneTypeName.value = name
              return detection_zone_colors_by_name_solid[name] || '#000000'
            })

            // Checkout queue (line length from waiting-zone occupancy overlap) —
            // only meaningful for Checkout Waiting zones (DetectionZoneTypeId 4).
            // Per-checkout demo values sampled from real store cid 3.
            const CHECKOUT_QUEUE_BY_ZONE = {
              'Checkout 1 Waiting': { mean_line: 0.37, peak_line: 5, pct_time_ge2: 7.3, avg_line_while_serving: 0.56, visits: 738 },
              'Checkout 2 Waiting': { mean_line: 0.21, peak_line: 4, pct_time_ge2: 3.4, avg_line_while_serving: 0.42, visits: 512 },
            }
            const checkoutQueue = computed(() => {
              if (detectionZoneTypeId.value !== 4) return null
              return CHECKOUT_QUEUE_BY_ZONE[props.zoneName] || CHECKOUT_QUEUE_BY_ZONE['Checkout 1 Waiting']
            })
            const checkoutQueueVerdict = computed(() => {
              const p = Number(checkoutQueue.value?.pct_time_ge2 ?? 0)
              if (p < 5) return t('demo.zone.queue.rarely')
              if (p < 15) return t('demo.zone.queue.occasionally')
              return t('demo.zone.queue.often')
            })

            const formattedTopVisitorDays = computed(() => {
                if (!results.value.top_visitor_days || !Array.isArray(results.value.top_visitor_days)) return []

                return results.value.top_visitor_days.map(dateStr => {
                    const date = new Date(dateStr)
                    const weekday = date.toLocaleDateString(dateLocale(), { weekday: 'short' })
                    const day = date.getDate()
                    const month = date.toLocaleDateString(dateLocale(), { month: 'long' })
                    return `${weekday}, ${day} ${month}`
                })
            })

            const formattedTopVisitorHours = computed(() => {
                if (!results.value.top_visitor_hours || !Array.isArray(results.value.top_visitor_hours)) return []

                return results.value.top_visitor_hours.map(datetimeStr => {
                    const date = new Date(datetimeStr)
                    const hour = date.getHours()
                    const nextHour = (hour + 1) % 24

                    const weekday = date.toLocaleDateString(dateLocale(), { weekday: 'short' })
                    const day = date.getDate();
                    const month = date.toLocaleDateString(dateLocale(), { month: 'long' })

                    return `${weekday}, ${day} ${month} | ${hourSpan(hour, nextHour)}`
                })
            })

            const formattedBusiestAvgHours = computed(() => {
                if (!results.value.busiest_avg_hours || !Array.isArray(results.value.busiest_avg_hours)) return [];

                return results.value.busiest_avg_hours.map(hour => {
                    const nextHour = (hour + 1) % 24;
                    return hourSpan(hour, nextHour);
                })
            })

            /* ============================================================
             * Tripwire-only mode (Customer Service Desk in the demo).
             *
             * A zone with tripwires and no polygon hides the polygon-derived
             * panels (dwell/segmentation) and renders a dedicated grid built
             * from the backend's tripwire-equivalent fields:
             *   tripwire_total_in/out, tripwire_chart_data,
             *   tripwire_heatmap_chart_data, tripwire_trend_total,
             *   tripwire_dwell_estimate_seconds + confidence + sample_days.
             * Mirrors production DetectionZoneData.vue.
             * ============================================================ */
            const zoneHasTripwire = computed(() => hasAnyTripwire(detectionZone.value?.DetectionZoneTripwirePoints))
            const zoneHasPolygon  = computed(() => {
                const p = detectionZone.value?.Polygon
                return Array.isArray(p) && p.length >= 3
            })
            const useTripwireMode = computed(() => zoneHasTripwire.value && !zoneHasPolygon.value)

            const useTripwireForDwell = computed(() => {
                const s = detectionZone.value?.Settings
                if (!s) return false
                if (typeof s === 'object') return !!s.UseTripwireForDwell
                try { return !!JSON.parse(s).UseTripwireForDwell } catch { return false }
            })

            // In/Out crossing labels — Entrance/Exit/Combined EE get
            // store-traffic phrasing; other zone types get generic In/Out.
            const tripwireInLabel = computed(() => {
                const t = detectionZoneTypeName.value
                if (t === 'Combined Entrance Exit Zone') return tr('demo.zone.direction.inStore')
                if (t === 'Entrance Zone') return tr('demo.zone.direction.storeEnter')
                if (t === 'Exit Zone') return tr('demo.zone.direction.wrongWay')
                return tr('demo.zone.direction.inbound')
            })
            const tripwireOutLabel = computed(() => {
                const t = detectionZoneTypeName.value
                if (t === 'Combined Entrance Exit Zone') return tr('demo.zone.direction.outStore')
                if (t === 'Entrance Zone') return tr('demo.zone.direction.wrongWay')
                if (t === 'Exit Zone') return tr('demo.zone.direction.wrongWay')
                return tr('demo.zone.direction.outbound')
            })

            const ENTRANCE_LIKE_TYPES = ['Entrance Zone', 'Exit Zone', 'Combined Entrance Exit Zone']
            const isEntranceLikeZoneType = computed(() =>
                ENTRANCE_LIKE_TYPES.includes(detectionZoneTypeName.value)
            )
            // For entrance-like tripwire zones the metric is total in-store
            // visit length, not zone-local dwell. The title is swapped so
            // users don't confuse the two metrics. Mirrors production
            // DetectionZoneData.vue.
            const dwellPanelTitle = computed(() =>
                isEntranceLikeZoneType.value
                    ? t('demo.zone.dwell.titleStore')
                    : t('demo.zone.dwell.title')
            )
            const useCrossingsLabel = computed(() =>
                useTripwireMode.value && !ENTRANCE_LIKE_TYPES.includes(detectionZoneTypeName.value)
            )
            const crossingsPanelTitle = computed(() =>
                ENTRANCE_LIKE_TYPES.includes(detectionZoneTypeName.value) ? t('demo.zone.panel.visits') : t('demo.zone.panel.crossings')
            )
            const trendsPanelTitle    = computed(() => useCrossingsLabel.value ? t('demo.zone.panel.crossingTrends') : t('demo.zone.panel.visitorTrends'))
            const topHighlightsTitle  = computed(() => useCrossingsLabel.value ? t('demo.zone.panel.topCrossing') : t('demo.zone.panel.topVisitor'))
            const avgComparisonTitle  = computed(() => useCrossingsLabel.value ? t('demo.zone.panel.avgCrossing') : t('demo.zone.panel.avgVisitor'))

            const tripwireGranularity = ref('daily')
            const updateTripwireGranularity = (g) => { tripwireGranularity.value = g }
            const tripwireChartData = computed(() => {
                const r = results.value || {}
                if (tripwireGranularity.value === 'hourly') {
                    return r.tripwire_chart_hourly_data || r.tripwire_chart_data || null
                }
                return r.tripwire_chart_data || null
            })

            // Settings step 2c: "Something changed at this spot" days as a dashed line on
            // the time charts (?scenario=zone_marker; production reads GET /zone_markers/).
            const zoneMarkers = computed(() => demoMarkersFor(route.query, props.zoneName))
            const zoneAxis = (data) => Object.entries(data || {})
                .filter(([, z]) => z && z[props.zoneName]).map(([d]) => d)
            const countsMarkerCaption = computed(() => markerCaption(zoneMarkers.value, zoneAxis(countsChartData.value)))
            const avgDurMarkerCaption = computed(() => markerCaption(zoneMarkers.value, zoneAxis(avgDurChartData.value)))
            const tripwireMarkerCaption = computed(() =>
                markerCaption(zoneMarkers.value, Object.keys(tripwireChartData.value || {}).sort()))

            // Effective sources flip to tripwire equivalents when polygon
            // panels are hidden.
            const effectiveHeatmapData = computed(() =>
                useTripwireMode.value
                    ? results.value?.tripwire_heatmap_chart_data
                    : results.value?.heatmap_chart_data
            )
            const effectiveTrendTotal = computed(() =>
                useTripwireMode.value
                    ? results.value?.tripwire_trend_total
                    : results.value?.trend_total
            )
            const effectiveAvgPerDay = computed(() =>
                useTripwireMode.value
                    ? results.value?.tripwire_avg_inbound_per_day
                    : results.value?.avg_visitors_per_day
            )
            const effectiveAvgSameWeekday = computed(() =>
                useTripwireMode.value
                    ? results.value?.tripwire_avg_inbound_same_weekday
                    : results.value?.avg_visitors_same_weekday
            )
            const effectiveMaxHourCount = computed(() =>
                useTripwireMode.value
                    ? results.value?.tripwire_max_inbound_hour
                    : results.value?.max_visitors_hour
            )
            const effectiveMaxDayCount = computed(() =>
                useTripwireMode.value
                    ? results.value?.tripwire_max_inbound_day
                    : results.value?.max_visitors_day
            )
            const effectiveMaxAvgPerHour = computed(() =>
                useTripwireMode.value
                    ? results.value?.tripwire_max_avg_inbound_per_hour
                    : results.value?.max_avg_visitors_per_hour
            )

            // Reformat tripwire top-days/hours strings the same way the
            // polygon equivalents already do.
            const effectiveFormattedTopVisitorDays = computed(() => {
                if (!useTripwireMode.value) return formattedTopVisitorDays.value
                const arr = results.value?.tripwire_top_inbound_days
                if (!Array.isArray(arr)) return []
                return arr.map(day => {
                    const date = new Date(day)
                    if (isNaN(date.getTime())) return ''
                    const weekday = date.toLocaleString(dateLocale(), { weekday: 'short' })
                    const d = String(date.getDate()).padStart(2, '0')
                    const m = date.toLocaleString(dateLocale(), { month: 'short' })
                    return `${weekday}, ${d} ${m}`
                })
            })
            const effectiveFormattedTopVisitorHours = computed(() => {
                if (!useTripwireMode.value) return formattedTopVisitorHours.value
                const arr = results.value?.tripwire_top_inbound_hours
                if (!Array.isArray(arr)) return []
                return arr.map(h => {
                    const date = new Date(h)
                    if (isNaN(date.getTime())) return ''
                    const hour = date.getHours()
                    const nextHour = (hour + 1) % 24
                    const weekday = date.toLocaleString(dateLocale(), { weekday: 'short' })
                    const day = String(date.getDate()).padStart(2, '0')
                    const month = date.toLocaleString(dateLocale(), { month: 'short' })
                    return `${weekday}, ${day} ${month} | ${hourSpan(hour, nextHour)}`
                })
            })
            const effectiveFormattedBusiestAvgHours = computed(() => {
                if (!useTripwireMode.value) return formattedBusiestAvgHours.value
                const arr = results.value?.tripwire_busiest_avg_inbound_hours
                if (!Array.isArray(arr)) return []
                return arr.map(hour => {
                    const nextHour = (hour + 1) % 24
                    return hourSpan(hour, nextHour)
                })
            })

            // True when the zone carries any tripwire crossings — drives
            // the optional Row 2.5 Crossings panel for combined polygon +
            // tripwire zones (e.g. Garden Utilities).
            const hasTripwireActivity = computed(() => {
                const r = results.value || {}
                return (Number(r.tripwire_total_in)  || 0) > 0
                    || (Number(r.tripwire_total_out) || 0) > 0
            })

            const tripwirePctOfTotal = computed(() => {
                const inbound = Number(results.value?.tripwire_total_in || 0)
                const total = Number(totalStoreVisitors.value || 0)
                if (!total) return 0
                return (inbound / total) * 100
            })

            // Format a dwell estimate (in seconds) as "Xm Ys" or "Ys"
            const formatDwellSeconds = (sec) => {
                const s = Math.max(0, Math.round(Number(sec) || 0))
                // Dutch: the shared duration format (the panel title already says "ongeveer").
                if (currentLanguage() === 'nl') return formatDuration(s)
                if (s < 60) return `${s}s`
                const m = Math.floor(s / 60)
                const r = s % 60
                return r ? `${m}m ${r}s` : `${m}m`
            }
            const dwellMethodTooltip = "Mode C (Little's Law): Avg dwell = (people inside) ÷ (arrival rate). " +
                "Computed from tripwire in/out pairs over the period."
            const dwellConfidenceTooltip = computed(() => {
                const r = results.value || {}
                if (!r.tripwire_dwell_confidence) return ''
                const bias = Number(r.tripwire_dwell_residual_direction != null ? 0 : 0)
                return `Confidence: ${r.tripwire_dwell_confidence}. ${dwellMethodTooltip}`
            })

            const loadJSON = (path, opts) => loadDemoJSON(path, opts)
            
            onMounted(async () => {
                isMounted.value = true  
                isLoading.value = true
                const { detectionzones } = await loadJSON('/demo_data/demo_detection_zones.json')
                allZones.value = detectionzones
                const { detectionzonetypes: ztypes } = await loadJSON('/demo_data/demo_detection_zone_types.json')
                detectionzonetypes.value = ztypes ?? []
                openingHoursMode.value = 'only_regular_opening_hours'
                disabledOpeningHourModes.value = [
                    'all',
                    'regular_and_irregular_opening_hours'
                ]

                duration.value = 30
                disabledDurations.value = [
                    '1', '7', '365', '1000', '2000', '3000', '4000'
                ]

                await loadData()
                            isLoading.value = false
                        })

                        const fileMap = {
              'Promoted Articles Zone': '/demo_data/demo_promo_zone_analytics.json',
              'Checkout 1 Interaction': '/demo_data/demo_checkout_1_interaction_analytics.json',
              'Customer Service Desk': '/demo_data/demo_service_zone_analytics.json',
              'Garden Utilities': '/demo_data/demo_garden_utilities_analytics.json'
            }

            const loadData = async () => {
              try {
                isLoading.value = true

                // find zone by name (we navigate with :zoneName)
                const zone = allZones.value.find(z => z.DetectionZoneName === props.zoneName)
                if (!zone) {
                  console.warn('Detection Zone not found for name:', props.zoneName)
                  results.value = {}
                  return
                }

                detectionZone.value = zone
                detectionZoneTypeId.value = zone.DetectionZoneTypeId

                // settings are under "Settings" in your demo_detection_zones.json
                let settings = zone.Settings || {}
                if (typeof settings === 'string') {
                  try { settings = JSON.parse(settings) } catch { settings = {} }
                }

                sameIdInOutSetting.value = settings.SameIdInOutUniqueData ?? false
                conversionRate.value = settings.EngagersConversionRate ?? 0.1
                productValue.value = settings.AverageProductValue ?? 0

                // Service response (product/demo zones only). Loaded BEFORE the
                // zone's own analytics file so a zone with no analytics mapping
                // still shows this panel — and a missing entry means "not enabled",
                // never a zero. Failure here must not take the whole page down.
                // Checkout throughput for a Checkout Interaction zone.
                checkoutTransactions.value = null
                if (detectionZoneTypeId.value === 5) {
                    try {
                        checkoutTransactions.value =
                            await loadJSON('/demo_data/demo_checkout_transactions.json')
                    } catch (e) {
                        console.warn('Demo checkout-transaction data unavailable:', e)
                    }
                }

                serviceResponse.value = null
                if ([6, 7].includes(detectionZoneTypeId.value)) {
                    try {
                        const sr = await loadJSON('/demo_data/demo_service_response.json')
                        serviceResponse.value = sr?.byZone?.[props.zoneName] ?? { enabled: false }
                    } catch (e) {
                        console.warn('Demo service-response data unavailable:', e)
                        serviceResponse.value = { enabled: false }
                    }
                }

                const file = fileMap[props.zoneName]
                if (!file) throw new Error(`No demo file mapped for zone "${props.zoneName}"`)

                results.value = await loadJSON(file)

                // store_total_visitors backs the "% of Total Visitors" card,
                // which renders for every zone type except Entrance / Exit /
                // Combined EE (1/2/3) — their inbound IS the store total, so
                // the share would always be 100% and the card stays hidden.
                if (![1, 2, 3].includes(detectionZoneTypeId.value)) {
                  totalStoreVisitors.value = (await loadJSON('/demo_data/demo_fixed_widgets.json')).store_total_visitors
                }
              } catch (err) {
                console.error('Error loading data:', err)
                results.value = {}
              } finally {
                isLoading.value = false
              }
            }

            watch(() => props.zoneName, async () => {
              storeAnalytics.value = ""
              await loadData()
            })

            const updateCountsGranularity = async (granularity) => {
                countsGranularity.value = granularity
            }

            const updateAvgDurGranularity = async (granularity) => {
                avgDurGranularity.value = granularity
            }

            onUnmounted(() => {
                isMounted.value = false
            })

            return {
                typeChange, NOTICES, ZONE_TYPE_NAMES, zoneTypeLabel, num, pctText, openingHoursChanged, thresholdChanged,
                zoneMarkers, countsMarkerCaption, avgDurMarkerCaption, tripwireMarkerCaption,
                isLoading,
                storeAnalytics,
                showAllensInsights,
                detectionZone,
                detectionZoneTypeName,
                serviceResponse,
                checkoutTransactions,
                detectionZoneTypeId,
                checkoutQueue,
                checkoutQueueVerdict,
                sameIdInOutSetting,
                zoneColor,
                results,
                formattedTopVisitorDays,
                formattedTopVisitorHours,
                formattedBusiestAvgHours,
                openingHoursMode,
                hideDwellInAllMode,
                dwellAllModeNote,
                disabledOpeningHourModes,
                updateCountsGranularity,
                countsGranularity,
                countsChartData,
                weatherOverlay,
                weatherByDate,
                tripwireWeatherByDate,
                updateAvgDurGranularity,
                avgDurGranularity,
                avgDurChartData,
                totalStoreVisitors,
                conversionRate,
                productValue,
                startDate,
                endDate,
                duration,
                durationText,
                dateRangeMode,
                disabledDurations,
                isInsightsDisabled,

                // Tripwire-only mode
                zoneHasTripwire,
                zoneHasPolygon,
                useTripwireMode,
                useTripwireForDwell,
                isEntranceLikeZoneType,
                dwellPanelTitle,
                tripwireInLabel,
                tripwireOutLabel,
                useCrossingsLabel,
                crossingsPanelTitle,
                trendsPanelTitle,
                topHighlightsTitle,
                avgComparisonTitle,
                tripwireGranularity,
                updateTripwireGranularity,
                tripwireChartData,
                effectiveHeatmapData,
                effectiveTrendTotal,
                effectiveAvgPerDay,
                effectiveAvgSameWeekday,
                effectiveMaxHourCount,
                effectiveMaxDayCount,
                effectiveMaxAvgPerHour,
                effectiveFormattedTopVisitorDays,
                effectiveFormattedTopVisitorHours,
                effectiveFormattedBusiestAvgHours,
                hasTripwireActivity,
                tripwirePctOfTotal,
                formatDwellSeconds,
                dwellMethodTooltip,
                dwellConfidenceTooltip,
            }

        }

    }
</script>

<style scoped>
/* 6-col grid so two x 3-span = side-by-side; 6-span = full width */
.row2-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;
}

.row2-item { min-width: 0; }       /* allow charts to shrink nicely */
.row2-span-3 { grid-column: span 3; }
.row2-span-6 { grid-column: span 6; }

.zone-row4 {
  /* ChartGrid already sets display:grid; we only override columns/gap */
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

/* Small note under header */
.same-id-note{
  color:#C24500; font-size:.9rem; padding-left:.5rem; margin:.4rem 0 .2rem;
}

/* Checkout queue card — line length in a checkout's waiting zone */
.checkout-queue-card{
  margin: 14px 0 2px;
  padding: 12px 14px;
  background: #fbf1f0;
  border: 1px solid #f0d9d7;
  border-left: 4px solid #AD3935;
  border-radius: 12px;
}
.checkout-queue-card .cq-head{
  display: inline-flex; align-items: center; gap: 7px;
  font-size: .8rem; font-weight: 700; text-transform: uppercase; letter-spacing: .5px;
  color: #AD3935; margin-bottom: 5px;
}
.checkout-queue-card .cq-body{
  margin: 0; font-size: .92rem; line-height: 1.5; color: #6b5250;
}
.checkout-queue-card .cq-body .cq-verdict{ color: #8f2b23; font-weight: 700; }

/* Inline toolbar area atop a panel (for the granularity radios) */
.grid-panel-toolbar{
  display: flex; align-items: center; justify-content: center; gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.wx-overlay-toggle{
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 0.78rem; color: #178f8f; cursor: pointer;
  padding: 3px 9px; border: 1px solid #d9ece9; border-radius: 999px;
  background: #f3faf9; user-select: none; margin-top: 6px;
}
.wx-overlay-toggle input{ accent-color: #1f9d9d; cursor: pointer; }
.wx-overlay-toggle .bi{ font-size: 0.85rem; }

/* Only affect this Trends panel */
.panel-trends :deep(.panel-body){
  height: auto;           /* override var(--panel-h) result */
  min-height: 120px;      /* keeps a nice base height at wide screens */
}

.panel-top-visitors:deep(.panel-body) {
  height: auto;            /* override fixed height */
  min-height: 120px;       /* keep a tidy base height */
}

/* Let content define its own height when parent is height:auto */
.panel-trends :deep(.panel-slot){
  height: auto;
}

.chart-vert-stack{
  display: grid;
  grid-auto-rows: minmax(0, auto);
  gap: 12px;         /* match ChartGrid gap */
}

/* Empty state text for panels where strategy is insufficient */
.empty-fallback{
  margin-top: 140px; text-align: center; color: #425461; font-style: italic;
}

/* Wrapper */
.dashboard-wrapper { width:100%; overflow-x:hidden; padding-bottom: 40px;}

/* Loader overlay (keep) */
.loader-overlay {
  position: absolute; top:0; left:0; width:100%; height:100%;
  display:flex; align-items:center; justify-content:center;
  background-color: rgba(255,255,255,0.8); z-index: 1000;
}
.loader { width:160px; margin-top:0; padding:2px; aspect-ratio:1; border-radius:50%;
  background:#B2C149;
  --_m:conic-gradient(#0000 10%,#000),linear-gradient(#000 0 0) content-box;
  -webkit-mask:var(--_m); mask:var(--_m);
  -webkit-mask-composite:source-out; mask-composite:subtract;
  animation: spinner 1s infinite linear;
}
@keyframes spinner { to { transform: rotate(1turn) } }

/* Modal (keep) */
.toggle-popup:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none; /* redundant for buttons, but keeps hover effects off */
}

.modal-overlay {
  position: fixed; inset:0; background: rgba(0,0,0,0.5);
  display:flex; align-items:center; justify-content:center; z-index:2100; /* above nav sidebar (1100) */
}
.modal-window {
  /* centered over the full viewport — no sidebar offset; fluid min-width */
  background:white; border-radius:8px; min-width:min(400px, 92vw); max-width:92vw; max-height:90vh;
  overflow:auto; padding:1rem; position:relative; box-shadow:0 4px 12px rgba(0,0,0,0.3);
}

/* 3-up grid for the first row */
.chart-grid--3 { display:grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 12px; }
@media (max-width: 1520px){ 
  .chart-grid--3 { grid-template-columns: repeat(2, minmax(0,1fr)); }
  .row2-grid { grid-template-columns: 1fr; }
  .row2-span-3, .row2-span-6 { grid-column: 1 / -1; }
  .zone-row4 {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 1250px){ .chart-grid--3 { grid-template-columns: 1fr; } }

/* 2-up inner grid for the split “per day” panels (collapses nicely) */
.chart-grid--2 { display:grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 12px; }
@media (max-width: 1250px){ .chart-grid--2 { grid-template-columns: 1fr; } }

/* ============================================================
   Tripwire-only mode grid (mirrors production DetectionZoneData.vue).
   ============================================================ */
.tripwire-layout-grid{
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  grid-template-rows: auto auto;
  grid-template-areas:
    "crossings crossings crossings crossings highlights highlights"
    "heatmap   heatmap   heatmap   trends-stk trends-stk trends-stk";
  gap: 16px;
  align-items: start;
}
.tw-area-crossings    { grid-area: crossings; }
.tw-area-highlights   { grid-area: highlights; }
.tw-area-heatmap      { grid-area: heatmap; }
.tw-area-trends-stack {
  grid-area: trends-stk;
  display: flex; flex-direction: column; gap: 16px;
}
.tw-area-highlights :deep(.mini-cards){ grid-template-columns: 1fr; }
.tripwire-layout-grid.tripwire-hourly{
  grid-template-rows: auto auto auto;
  grid-template-areas:
    "crossings  crossings  crossings  crossings  crossings  crossings"
    "highlights highlights highlights heatmap    heatmap    heatmap"
    "trends-stk trends-stk trends-stk trends-stk trends-stk trends-stk";
}
.tripwire-layout-grid.tripwire-hourly .tw-area-trends-stack{ flex-direction: row; }
.tripwire-layout-grid.tripwire-hourly .tw-area-trends-stack > *{ flex: 1; }
/* 1250 (not 1024) — mirrors production: stack the tripwire grid before the
   crossings toolbar wraps / the side cards get too narrow. */
@media (max-width: 1250px){
  .tripwire-layout-grid,
  .tripwire-layout-grid.tripwire-hourly{
    grid-template-columns: 1fr;
    grid-template-areas:
      "crossings"
      "highlights"
      "heatmap"
      "trends-stk";
  }
  .tripwire-layout-grid.tripwire-hourly .tw-area-trends-stack{ flex-direction: column; }
}
.tw-compact :deep(.panel-body){ padding: 8px 10px; }
.tw-compact :deep(.panel-head){ padding: 6px 10px; }

.pct-of-total{
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  height: 100%; gap: 4px;
}
.pct-of-total .pct-value{ font-size: 2.4rem; font-weight: 700; line-height: 1; }
.pct-of-total .pct-context{ font-size: 0.85rem; color: #6b7a86; text-align: center; }

.dwell-estimate{
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  height: 100%; gap: 6px; text-align: center;
}
.dwell-value-row{ display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; justify-content: center; }
.dwell-value{ font-size: 2.1rem; font-weight: 700; line-height: 1; font-variant-numeric: tabular-nums; }
.dwell-conf{
  display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 999px;
  font-size: .68rem; font-weight: 700; text-transform: uppercase; letter-spacing: .3px;
}
.dwell-conf.conf-high{   color: #2f6f3a; background: #f4faf5; border: 1px solid #bfe2c7; }
.dwell-conf.conf-medium{ color: #9a6b00; background: #fff8e6; border: 1px solid #ffd97a; }
.dwell-context{
  display: inline-flex; align-items: center; gap: 4px; font-size: .78rem; color: #6b7a86;
}
.dwell-context i{ opacity: .7; }

.grid-panel-toolbar.tripwire-toolbar{ justify-content: space-between; flex-wrap: wrap; gap: 10px; }
.tripwire-totals{ display: flex; gap: 10px; flex-wrap: wrap; }
.tw-pill{
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 10px; border-radius: 999px;
  font-size: .85rem; color: #2b3a44;
  border: 1px solid #e6edf3; background: #f9fbfd;
}
.tw-pill i{ opacity: .8 }
.tw-pill.tw-in{ color: #4F7EB3 }
.tw-pill.tw-out{ color: #6C9046 }
.tw-pill b{ color: #2b3a44; font-weight: 600 }

</style>